
import ResetButton from '@/components/buttons/ResetButton.vue';
import { AppDirection } from '@/enums/app-direction';
import { CollectionType } from '@/enums/collection-type';
import { WebLocale } from '@/enums/web-locale';
import { isFilterActive } from '@/helpers/filter-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { FilterCollection, FilterName } from '@/stores/filter.store';
import ToggleButton from '@/ui-components/ToggleButton.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const Filter = namespace('filter');
const Language = namespace('language');
const Meta = namespace('meta');

@Component({
	name: 'FilterPrice',
	components: { ResetButton, ToggleButton },
})
export default class FilterPrice extends Vue {
	@Prop() collectionType: CollectionType;
	@Prop() currentFilters: FilterCollection;

	@Language.State webLocale: string;

	@Meta.Getter appDirection: AppDirection;

	@Filter.Action resetFilters: (payload: { filterNames: FilterName[] }) => void;
	@Filter.Action setRange: ({
		rangeName,
		trackingKey,
		upper,
		lower,
		selectedState,
	}: {
		rangeName: 'price' | 'release_year';
		trackingKey: string;
		upper: number | null;
		lower: number | null;
		selectedState?: 1 | 0;
	}) => void;

	get MAX_PRICE() {
		return 50 * this.currencyAmplifier;
	}

	get currencyAmplifier(): number {
		return [
			{ webLocale: WebLocale.jp, amplifier: 100 },
			{ webLocale: WebLocale.kr, amplifier: 1000 },
			{ webLocale: WebLocale.in, amplifier: 50 },
			{ webLocale: WebLocale.br, amplifier: 5 },
		]
			.filter(item => this.webLocale === item.webLocale)
			.concat([{ webLocale: WebLocale.de, amplifier: 1 }]) // weblocale here is not important
			.reduce((prev, curr) => curr.amplifier * prev, 1);
	}

	get price() {
		return {
			min: 0,
			max: this.MAX_PRICE,
			step: 1,
			value: {
				lower: 0,
				upper: this.MAX_PRICE,
			},
		};
	}

	/**
	 * min_price and max_price are used to send it to the filter route.
	 */
	get presets() {
		return [1, 2, 3, 5]
			.map(item => ({
				min_price: 0,
				max_price: (item * this.currencyAmplifier) as number | null,
				label: this.$t('WEBAPP_PRICEFILTER_UPTO', { x: item * this.currencyAmplifier }),
			}))
			.concat({
				min_price: 5 * this.currencyAmplifier,
				max_price: null,
				label: this.$t('WEBAPP_PRICEFILTER_MORETHAN', { x: 5 * this.currencyAmplifier }),
			});
	}

	get currentPriceFilter() {
		return {
			min_price: this.currentFilters.min_price,
			max_price: this.currentFilters.max_price,
		};
	}

	mounted() {
		// setting the values of the prices in mounted because this.currentFilter is not initialized before somehow
		this.price.value.lower = this.currentPriceFilter.min_price || this.price.min;
		this.price.value.upper = this.currentPriceFilter.max_price || this.price.max;

		// double knobs, with upper and lower values, does not work correctly and we have to manually set the value on the web component.
		this.updateRangeSlider({ lower: this.price.value.lower, upper: this.price.value.upper });
	}

	isFilterActive(filterName: FilterName, value: any) {
		return isFilterActive(filterName, value, this.currentFilters);
	}

	@Watch('currentPriceFilter')
	changedPriceFilters({ min_price, max_price }: { min_price: number; max_price: number }) {
		this.updateRangeSlider({ lower: min_price || this.price.min, upper: max_price || this.price.max });
	}

	rangeChanged(evt: any) {
		const value = evt.target?.value as { lower: number; upper: number } | undefined;
		// ignore click events outside filter
		if (value === undefined) {
			return;
		}
		if (value.upper === this.price.value.upper && value.lower === this.price.value.lower) {
			return;
		}

		this.setRange({
			rangeName: 'price',
			trackingKey: 'knob',
			upper: value.upper === this.price.max ? null : value.upper,
			lower: value.lower === this.price.min ? null : value.lower,
		});
	}

	setPreset(preset: any) {
		if (this.isPriceFilterActive(preset.min_price, preset.max_price)) {
			const trackingKey = `${preset.label}-${preset.value}`;
			this.resetFilters({ filterNames: ['max_price', 'min_price'] });
			TrackingHelper.trackEvent('userinteraction', {
				action: 'filter_toggled',
				label: 'price',
				property: trackingKey,
				value: 0,
			});
		} else {
			const trackingKey = `${preset.label}-${preset.value}`;
			this.setRange({
				rangeName: 'price',
				trackingKey,
				upper: preset.max_price,
				lower: preset.min_price,
				selectedState: 1,
			});
		}
	}

	isPriceFilterActive(min: number, max: number | null) {
		const minPrice = this.currentFilters.min_price;
		const maxPrice = this.currentFilters.max_price;
		return min === minPrice && maxPrice === max;
	}

	updateRangeSlider(range: { lower: number; upper: number }) {
		(this.$refs['range'] as any).value = range;
	}

	formatPrice(price: number) {
		return this.appDirection === AppDirection.RTL
			? price + this.$tc('WEBAPP_STANDARD_CURRENCY')
			: this.$tc('WEBAPP_STANDARD_CURRENCY') + price;
	}
}
